/**
 * Turn an AI logo drawn on plain white into a transparent, tightly cropped
 * logo. Pure functions over ImageData-shaped pixels (RGBA, row by row), so
 * they can be unit-tested without a browser; src/lib/logo-image.ts does the
 * canvas work around them.
 *
 * 1. Background: every near-white pixel (each channel within `tolerance` of
 *    255, or already transparent) goes, including the white inside letters
 *    like "a", "d" and "o"; otherwise those holes show as white dots on a dark
 *    header. White details inside a coloured shape become see-through, which
 *    looks identical on a light header (a standard logo knockout). Pass
 *    `keepEnclosedWhite` to only clear white connected to the edges.
 * 2. Soft edges: pixels on the border of the removed area are the logo's
 *    anti-aliasing, i.e. its colour blended with white. Each gets an alpha from
 *    how far it is from white compared with its solid neighbours, and the
 *    white is taken back out of its colour, so there's no white halo on a dark
 *    header.
 * 3. Trim to the visible content plus a little padding.
 */

export interface Pixels {
  width: number;
  height: number;
  /** RGBA, 4 bytes per pixel, row by row (like ImageData.data). */
  data: Uint8ClampedArray;
}

export interface BackgroundOptions {
  /** How far (0-255, per channel) from pure white still counts as background. */
  tolerance?: number;
  /** Alpha at or below this counts as background too. */
  transparentBelow?: number;
  /** Only clear white connected to the image edges (keeps enclosed white). Default false. */
  keepEnclosedWhite?: boolean;
}

/** Distance from white: the largest per-channel shortfall from 255. */
function whiteDistance(data: Uint8ClampedArray, i: number): number {
  return Math.max(255 - data[i], 255 - data[i + 1], 255 - data[i + 2]);
}

/** A copy with the white background (and, by default, enclosed white) made transparent (same size). */
export function removeWhiteBackground(input: Pixels, options: BackgroundOptions = {}): Pixels {
  const { tolerance = 24, transparentBelow = 8, keepEnclosedWhite = false } = options;
  const { width, height } = input;
  const data = new Uint8ClampedArray(input.data);
  const count = width * height;
  if (!count) return { width, height, data };

  const isBackgroundLike = (p: number) => {
    const i = p * 4;
    return data[i + 3] <= transparentBelow || whiteDistance(data, i) <= tolerance;
  };

  // 1. Flood fill from the edges (iterative, 4-connected).
  const background = new Uint8Array(count);
  const stack = new Int32Array(count);
  let top = 0;
  const seed = (p: number) => {
    if (!background[p] && isBackgroundLike(p)) {
      background[p] = 1;
      stack[top++] = p;
    }
  };
  for (let x = 0; x < width; x++) {
    seed(x);
    seed((height - 1) * width + x);
  }
  for (let y = 0; y < height; y++) {
    seed(y * width);
    seed(y * width + width - 1);
  }
  while (top > 0) {
    const p = stack[--top];
    const x = p % width;
    if (x > 0) seed(p - 1);
    if (x < width - 1) seed(p + 1);
    if (p >= width) seed(p - width);
    if (p < count - width) seed(p + width);
  }
  if (!keepEnclosedWhite) {
    // Letter counters and other enclosed white go too.
    for (let p = 0; p < count; p++) if (!background[p] && isBackgroundLike(p)) background[p] = 1;
  }

  // 2. Soft edges on the pixels bordering the background (8-connected).
  const neighbours = (p: number, visit: (q: number) => void) => {
    const x = p % width;
    const y = (p - x) / width;
    for (let dy = -1; dy <= 1; dy++) {
      const ny = y + dy;
      if (ny < 0 || ny >= height) continue;
      for (let dx = -1; dx <= 1; dx++) {
        const nx = x + dx;
        if ((dx || dy) && nx >= 0 && nx < width) visit(ny * width + nx);
      }
    }
  };
  const border = new Uint8Array(count);
  for (let p = 0; p < count; p++) {
    if (background[p]) continue;
    let touches = false;
    neighbours(p, (q) => {
      if (background[q]) touches = true;
    });
    if (touches) border[p] = 1;
  }
  const edits: Array<[number, number, number, number, number]> = [];
  for (let p = 0; p < count; p++) {
    if (!border[p]) continue;
    const i = p * 4;
    const own = whiteDistance(data, i);
    // The solid colour nearby: the furthest-from-white neighbour that isn't background.
    // Interior (non-border) neighbours are preferred; thin lines fall back to border ones.
    let solid = 0;
    let solidBorder = own;
    neighbours(p, (q) => {
      if (background[q]) return;
      const d = whiteDistance(data, q * 4);
      if (border[q]) solidBorder = Math.max(solidBorder, d);
      else solid = Math.max(solid, d);
    });
    const reference = Math.max(solid || solidBorder, own);
    const alpha = reference > 0 ? own / reference : 1;
    if (alpha >= 1) continue;
    // Observed = alpha * colour + (1 - alpha) * white, so colour = 255 - (255 - observed) / alpha.
    const unblend = (v: number) => Math.round(255 - (255 - v) / alpha);
    edits.push([i, unblend(data[i]), unblend(data[i + 1]), unblend(data[i + 2]), Math.round(data[i + 3] * alpha)]);
  }
  for (const [i, r, g, b, a] of edits) {
    data[i] = r;
    data[i + 1] = g;
    data[i + 2] = b;
    data[i + 3] = a;
  }

  // The background itself: fully transparent (colour zeroed too, so nothing bleeds when scaled).
  for (let p = 0; p < count; p++) {
    if (background[p]) data.fill(0, p * 4, p * 4 + 4);
  }
  return { width, height, data };
}

export interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** The smallest box holding every pixel with alpha above `alphaThreshold`, or null if there are none. */
export function contentBounds(input: Pixels, alphaThreshold = 8): Box | null {
  const { width, height, data } = input;
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (data[(y * width + x) * 4 + 3] > alphaThreshold) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  return maxX < 0 ? null : { x: minX, y: minY, width: maxX - minX + 1, height: maxY - minY + 1 };
}

/**
 * Crop to the content plus `padding` pixels on each side (clamped to the
 * image). An image with no visible content is returned unchanged.
 */
export function trimToContent(input: Pixels, padding = 0, alphaThreshold = 8): Pixels {
  const bounds = contentBounds(input, alphaThreshold);
  if (!bounds) return input;
  const x0 = Math.max(0, bounds.x - padding);
  const y0 = Math.max(0, bounds.y - padding);
  const x1 = Math.min(input.width, bounds.x + bounds.width + padding);
  const y1 = Math.min(input.height, bounds.y + bounds.height + padding);
  const width = x1 - x0;
  const height = y1 - y0;
  const data = new Uint8ClampedArray(width * height * 4);
  for (let y = 0; y < height; y++) {
    const from = ((y0 + y) * input.width + x0) * 4;
    data.set(input.data.subarray(from, from + width * 4), y * width * 4);
  }
  return { width, height, data };
}

/**
 * The whole job: transparent background, then trimmed with padding of about
 * 3% of the logo's longer side (at least 2 px). `empty` is true when nothing
 * but background was found (the original is returned untouched then).
 */
export function makeLogoTransparent(input: Pixels, options: BackgroundOptions = {}): { pixels: Pixels; empty: boolean } {
  const cleared = removeWhiteBackground(input, options);
  const bounds = contentBounds(cleared);
  if (!bounds) return { pixels: input, empty: true };
  const padding = Math.max(2, Math.round(Math.max(bounds.width, bounds.height) * 0.03));
  return { pixels: trimToContent(cleared, padding), empty: false };
}
